import React, { useRef, useState, useEffect } from 'react';
import { Box, Typography, Paper, CircularProgress, Snackbar, Button, IconButton, TextField } from '@mui/material';
import { CheckCircle, ArrowBack } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import axiosInstance from 'utils/axiosInstance';
import { CameraSVG } from '@shared/cameraSVG';
import { GallerySVG } from '@shared/gallerySVG';
import { APIResource, KYCConstants, KYCRegxConstants } from 'constants/application';
import { getUrl } from 'utils/getURL';
import { UpdateBankModal } from '../modal/UpdateBankModal';
import { AssociationModal } from '../modal/AssociationModal';
import { DocumentViewerModal } from '../modal/DocumentViewerModal';
import { GstDocumentViewerModal } from '../modal/GstDocumentViewerModal';
import { DocumentTipsModal } from '../modal/DocumentTipsModal';
import { LoaderStageStatus, ScanningIcon } from './LoaderScreen';
import GstImageIcon from 'resources/img/Image.png';
import GstPdfIcon from 'resources/img/pdf.svg';
import { ReasonForMismatchModal } from '../modal/ReasonForMismatchModal';
import { NameMismatchDetailsModal } from '../modal/NameMismatchDetailsModal';
import { BankDetailsFormDefaultValues, IBankDetailsForm } from '../../bankDetails';
import { IBasicDetailsForm } from '../../basicDetails';
import { IAllData } from '../../KYCsteps';
import { setGenericInformation } from 'store/slices/genericInformation';
import { getValidateBankFailure, setBankResponse, setBankStatus, IValidateBankResult, ValidateBank } from 'store/slices/validateBank';
import { setBankDetails, clearBankDetails, ValidateIFSC } from 'store/slices/ifscSlice';
import { BANK_STATUS_VARIANTS } from 'constants/bankStatusVariants';
import { ErrorAlertIcon } from '../../../../shared/ErrorAlertIcon';
import { ErrorAlert } from '../../../../shared/ErrorAlert';
import { bankDetailsOCRStyles } from './BankDetailsOCR.styles';
import { resolveEstablishmentType } from '../../../../shared/nameMismatchUtil';
import { isNameMatch } from '../../../../shared/kyc-name-match';
import { useTranslation } from 'react-i18next';
import { useEnhancedWebcam } from '@shared/fileUploaderWithCamera/useEnhancedWebcam';
import { WebcamCaptureOverlay } from '@shared/fileUploaderWithCamera/WebcamCaptureOverlay';
import { resolveKycErrorMessage } from 'utils/kycErrorMessage';
import {
  fetchKycDocumentPresignedUrl,
  isRemoteDocumentDisplayUrl,
  isUsableDocumentDisplayUrl,
  resolveKycDocumentDisplayUrl,
} from 'utils/fetchKycDocumentPresignedUrl';
interface RootState {
  info: {
    allData: {
      customerId?: string;
      mobileNumber?: string;
      ownerName?: string;
      shopName?: string;
      establishmentType?: string;
      panDetails?: Record<string, unknown> | null;
      bankDetails?: Record<string, unknown> | null;
      gstDetails?: {
        gstDocumentKey?: string;
        gstNumber?: string;
        registeredBusinessName?: string;
      } | null;
    };
  };
  pan: {
    panResponse?: {
      fullName?: string;
      [key: string]: unknown;
    } | null;
  };
}

interface BankDetailsOCRProps {
  onBack: () => void;
  nextStep?: (data: IBankDetailsForm | IBasicDetailsForm) => void;
  initialState?: BankDetailsOCRPersistedState;
  onStateChange?: (state: BankDetailsOCRPersistedState) => void;
  establishmentType?: string;
  formData: IAllData;
  associatedCustomerDataList?: Array<{ customerId: string; firmName: string }>;
  panNumber?: string;
  prefillImageUrl?: { imageUrl: string; objectKey: string; customerId: string; accountType: string } | null;
  onPrefillImageConsumed?: () => void;
}

export interface BankDetailsOCRPersistedState {
  uploadedImageSrc: string | null;
  selectedAccountType: string;
  verifiedAccountHolderName: string;
  isBankVerified: boolean;
  nameMatched?: boolean;
  validationMessage: string;
  bankUploadFailureCount: number;
  isManualMode: boolean;
  manualBankAccountNumber: string;
  manualReEnterBankAccountNumber: string;
  manualIfscCode: string;
  manualIfscBankName: string;
  reasonForMismatch: string;
  customReasonForMismatch: string;
  errorMessage: string;
  showManualIfsc?: boolean;
  gstCertificateObjectKey?: string;
  gstCertificateFileName?: string;
  gstCertificatePublicUrl?: string;
  gstNumber?: string;
  gstFirmName?: string;
  isGstVerified?: boolean;
  gstValidationMessage?: string;
  hasDeletedGstCertificate?: boolean;
  manualIfscInput?: string;
  ifscValidationStatus?: 'idle' | 'loading' | 'valid' | 'invalid';
  ifscBankName?: string;
  ifscBranchName?: string;
  ocrAccountNumber?: string;
  ocrObjectKey?: string;
  manualIfscBankError?: string;
}

interface PresignedUrlResponse {
  expiresAt: string;
  objectKey: string;
  presignedUrl: string;
  publicUrl: string;
}

interface BankOcrResponse {
  [key: string]: unknown;
}

interface GstOcrResponse {
  [key: string]: unknown;
}

type LoaderStage = {
  text: string;
  status: LoaderStageStatus;
};

type NameMismatchDetailsScenario = 'proprietorship' | 'partnership_company' | 'gst_pan' | 'gst_business';

export const chequeNameMismatchMessage = "Bank account name doesn't match your PAN.";
export const bankDetailsVerifiedMessage = 'Bank details successfully verified.';

const VERIFY_STAGE_TRANSITION_DELAY_MS = 1000;
const MIN_VERIFY_STAGE_VISIBLE_MS = 1200;
const SUCCESS_TICK_VISIBLE_MS = 1600;
const ALLOWED_IMAGE_MIME_TYPES = ['image/jpeg', 'image/png'];
const ALLOWED_IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png'];

const wait = (ms: number) => new Promise((resolve) => { setTimeout(resolve, ms); });
const isAllowedImageFile = (file: File) => {
  const mimeType = String(file.type || '').toLowerCase();
  const extension = (file.name.split('.').pop() || '').toLowerCase();

  if (ALLOWED_IMAGE_MIME_TYPES.includes(mimeType)) {
    return true;
  }

  return ALLOWED_IMAGE_EXTENSIONS.includes(extension);
};

const getSource = () => localStorage.getItem('appname') || 'BRP';

const extractString = (data: Record<string, unknown>, keys: string[]) => {
  for (const key of keys) {
    const value = data[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

const parseNameMatched = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') {
      return true;
    }

    if (normalized === 'false') {
      return false;
    }
  }

  return undefined;
};

export function BankDetailsOCR({ onBack, nextStep, initialState, onStateChange , establishmentType, formData, associatedCustomerDataList, panNumber, prefillImageUrl, onPrefillImageConsumed}: BankDetailsOCRProps) {
  const { t } = useTranslation();
  const contentContainerRef = useRef<HTMLDivElement | null>(null);
  const galleryInputRef = useRef<HTMLInputElement | null>(null);
  const lastObjectUrlRef = useRef<string | null>(null);
  const pendingGalleryVerifyRef = useRef(false);
  const prefillVerificationStartedRef = useRef(false);
  const bankImageRetryUsedRef = useRef(false);
  const previousStateRef = useRef<{
    imageSrc: string | null;
    isBankVerified: boolean;
    verifiedAccountHolderName: string;
    validationMessage: string;
    bankResponse: any;
    bankStatus: string;
    bankDetails: any;
    ifscDetails: any;
  } | null>(null);

  const info = useSelector((state: RootState) => state?.info?.allData);
  const panValidation = useSelector((state: RootState) => state?.pan);
  const bankSlice = useSelector((state: any) => state?.bank);
  const ifscSlice = useSelector((state: any) => state?.ifsc);
  const dispatch = useDispatch();

  const [modalOpen, setModalOpen] = useState(false);
  const [tipsModalOpen, setTipsModalOpen] = useState(false);
  const [tipsAction, setTipsAction] = useState<'camera' | 'upload'>('camera');
  const [uploadedImageSrc, setUploadedImageSrc] = useState<string | null>(
    initialState?.uploadedImageSrc || null
  );
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [selectedAccountType, setSelectedAccountType] = useState(
    initialState?.selectedAccountType || formData?.accType || ''
  );
  const [verifiedAccountHolderName, setVerifiedAccountHolderName] = useState(
    initialState?.verifiedAccountHolderName || ''
  );
  const [isBankVerified, setIsBankVerified] = useState(
    initialState?.isBankVerified || false
  );
  const [nameMatched, setNameMatched] = useState<boolean | undefined>(
    initialState?.nameMatched
  );
  const [validationMessage, setValidationMessage] = useState(
    initialState?.validationMessage || ''
  );
  const [bankUploadFailureCount, setBankUploadFailureCount] = useState(
    initialState?.bankUploadFailureCount || 0
  );
  const [accountTypeError, setAccountTypeError] = useState('');
  const invalidFileTypeMessage = t('invalid_file_type');

  // Manual IFSC entry state (shown when OCR can't read IFSC)
  const [showManualIfsc, setShowManualIfsc] = useState(initialState?.showManualIfsc || false);
  const [manualIfscInput, setManualIfscInput] = useState(initialState?.manualIfscInput || '');
  const [ifscValidationStatus, setIfscValidationStatus] = useState<'idle' | 'loading' | 'valid' | 'invalid'>(initialState?.ifscValidationStatus || 'idle');
  const [ifscBankName, setIfscBankName] = useState(initialState?.ifscBankName || '');
  const [ifscBranchName, setIfscBranchName] = useState(initialState?.ifscBranchName || '');
  const [ifscError, setIfscError] = useState('');
  const [manualIfscBankError, setManualIfscBankError] = useState(initialState?.manualIfscBankError || '');
  const [ocrAccountNumber, setOcrAccountNumber] = useState(initialState?.ocrAccountNumber || '');
  const [ocrObjectKey, setOcrObjectKey] = useState(initialState?.ocrObjectKey || '');

  const [showLoader, setShowLoader] = useState(false);
  const [error, setError] = useState(initialState?.errorMessage || '');
  const [loaderTitle, setLoaderTitle] = useState(t('verifying_bank_details'));
  const [loaderStages, setLoaderStages] = useState<LoaderStage[]>([
    { text: t('fetching_bank_details'), status: 'pending' },
    { text: t('verifying_bank_details'), status: 'pending' },
  ]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [reasonModalOpen, setReasonModalOpen] = useState(false);
  const [reasonForMismatch, setReasonForMismatch] = useState<string>(
    initialState?.reasonForMismatch || formData?.nameMismatchReason || ''
  );
  const [customReasonForMismatch, setCustomReasonForMismatch] = useState<string>(
    initialState?.customReasonForMismatch || ''
  );
  const [nameMismatchDetailsOpen, setNameMismatchDetailsOpen] = useState(false);
  const [nameMismatchDetailsScenario, setNameMismatchDetailsScenario] = useState<NameMismatchDetailsScenario>('proprietorship');
  const [associationDetailsOpen, setAssociationDetailsOpen] = useState(false);
  const [gstCertificateFile, setGstCertificateFile] = useState<File | null>(null);
  const [gstCertificateObjectKey, setGstCertificateObjectKey] = useState<string>(initialState?.gstCertificateObjectKey || '');
  const [gstCertificateFileName, setGstCertificateFileName] = useState<string>(initialState?.gstCertificateFileName || '');
  const [gstCertificatePublicUrl, setGstCertificatePublicUrl] = useState<string>(initialState?.gstCertificatePublicUrl || '');
  const [gstCertificateUploading, setGstCertificateUploading] = useState(false);
  const [isGstVerifying, setIsGstVerifying] = useState(false);
  const [gstCertificateError, setGstCertificateError] = useState('');
  const [gstNumber, setGstNumber] = useState<string>(initialState?.gstNumber || '');
  const [gstFirmName, setGstFirmName] = useState<string>(initialState?.gstFirmName || '');
  const [isGstVerified, setIsGstVerified] = useState<boolean>(Boolean(initialState?.isGstVerified));
  const [gstValidationMessage, setGstValidationMessage] = useState<string>(initialState?.gstValidationMessage || '');
  const [gstValidationMismatchCode, setGstValidationMismatchCode] = useState('');
  const [hasDeletedGstCertificate, setHasDeletedGstCertificate] = useState(
    Boolean(initialState?.hasDeletedGstCertificate)
  );
  const [gstDocumentViewerOpen, setGstDocumentViewerOpen] = useState(false);
  const gstFileInputRef = useRef<HTMLInputElement | null>(null);
  const uploadedImageSrcRef = useRef<string | null>(uploadedImageSrc);
  const resolvedEstablishmentType = String(formData?.establishmentType || establishmentType || '').trim();
  const normalizedEstablishmentType = resolvedEstablishmentType.toUpperCase();

  const webcam = useEnhancedWebcam({
    onUsePhoto: (file, previewUrl) => {
      pendingGalleryVerifyRef.current = true;
      previousStateRef.current = {
        imageSrc: uploadedImageSrc,
        isBankVerified,
        verifiedAccountHolderName,
        validationMessage,
        bankResponse: bankSlice?.bankResponse,
        bankStatus: bankSlice?.bankStatus,
        bankDetails: info?.bankDetails,
        ifscDetails: ifscSlice?.bankDetails,
      };
      if (lastObjectUrlRef.current) {
        URL.revokeObjectURL(lastObjectUrlRef.current);
        lastObjectUrlRef.current = null;
      }
      setSelectedFile(file);
      setUploadedImageSrc(previewUrl);
      setVerifiedAccountHolderName('');
      setIsBankVerified(false);
      setNameMatched(undefined);
      setValidationMessage('');
      setError('');
      setLoaderTitle(t('verifying_bank_details'));
      setLoaderStages([
        { text: t('fetching_bank_details'), status: 'pending' },
        { text: t('verifying_bank_details'), status: 'pending' },
      ]);
      setReasonForMismatch('');
      setCustomReasonForMismatch('');
      setBankUploadFailureCount(0);
      setShowManualIfsc(false);
      setManualIfscInput('');
      setIfscValidationStatus('idle');
      setIfscBankName('');
      setIfscBranchName('');
      setIfscError('');
      setManualIfscBankError('');
      setOcrAccountNumber('');
      setOcrObjectKey('');
      resetGstStateForNewCheque();
      dispatch(clearBankDetails());
      setModalOpen(false);
    },
  });

  useEffect(() => {
    if (pendingGalleryVerifyRef.current && selectedFile && uploadedImageSrc) {
      pendingGalleryVerifyRef.current = false;
      handleVerifyWrapper();
    }
  }, [selectedFile, uploadedImageSrc]); // eslint-disable-line

  useEffect(() => {
    if (!showLoader) {
      return;
    }

    const timer = window.setTimeout(() => {
      const container = contentContainerRef.current;
      if (!container) {
        return;
      }

      container.scrollTo({
        top: container.scrollHeight,
        behavior: 'smooth',
      });
    }, 120);

    return () => {
      window.clearTimeout(timer);
    };
  }, [showLoader, loaderStages]);

  useEffect(() => {
    uploadedImageSrcRef.current = uploadedImageSrc;
  }, [uploadedImageSrc]);

  useEffect(() => {
    onStateChange?.({
      uploadedImageSrc:
        uploadedImageSrc || initialState?.uploadedImageSrc || null,
      selectedAccountType,
      verifiedAccountHolderName,
      isBankVerified,
      nameMatched,
      validationMessage,
      bankUploadFailureCount,
      isManualMode: false,
      manualBankAccountNumber: '',
      manualReEnterBankAccountNumber: '',
      manualIfscCode: '',
      manualIfscBankName: '',
      reasonForMismatch,
      customReasonForMismatch,
      errorMessage: error,
      showManualIfsc,
      manualIfscInput,
      ifscValidationStatus,
      ifscBankName,
      ifscBranchName,
      ocrAccountNumber,
      ocrObjectKey,
      manualIfscBankError,
      gstCertificateObjectKey,
      gstCertificateFileName,
      gstCertificatePublicUrl,
      gstNumber,
      gstFirmName,
      isGstVerified,
      gstValidationMessage,
      hasDeletedGstCertificate,
    });
  }, [
    uploadedImageSrc,
    initialState?.uploadedImageSrc,
    selectedAccountType,
    verifiedAccountHolderName,
    isBankVerified,
    nameMatched,
    validationMessage,
    bankUploadFailureCount,
    reasonForMismatch,
    customReasonForMismatch,
    error,
    showManualIfsc,
    manualIfscInput,
    ifscValidationStatus,
    ifscBankName,
    ifscBranchName,
    ocrAccountNumber,
    ocrObjectKey,
    manualIfscBankError,
    gstCertificateObjectKey,
    gstCertificateFileName,
    gstCertificatePublicUrl,
    gstNumber,
    gstFirmName,
    isGstVerified,
    gstValidationMessage,
    hasDeletedGstCertificate,
    onStateChange,
  ]);

   useEffect(() => {
    if (normalizedEstablishmentType === 'PARTNERSHIP' || normalizedEstablishmentType === 'COMPANY') {
      setSelectedAccountType('Current');
    } else {
      setSelectedAccountType('Savings');
    }
  }, [normalizedEstablishmentType]);

  useEffect(() => {
    // Parent may resolve the display URL after mount — sync it into local state.
    if (
      initialState?.uploadedImageSrc &&
      initialState.uploadedImageSrc !== uploadedImageSrc &&
      !uploadedImageSrc?.startsWith('blob:')
    ) {
      bankImageRetryUsedRef.current = false;
      setUploadedImageSrc(initialState.uploadedImageSrc);
    }
  }, [initialState?.uploadedImageSrc]);

  useEffect(() => {
    const objectKey =
      ocrObjectKey ||
      formData?.accountProofDocumentUrl ||
      (info?.bankDetails as { accountProofDocumentUrl?: string } | null | undefined)
        ?.accountProofDocumentUrl ||
      '';
    if (!objectKey || !info?.customerId) {
      return;
    }
    if (isUsableDocumentDisplayUrl(uploadedImageSrc)) {
      return;
    }

    let cancelled = false;
    (async () => {
      const displayUrl = await resolveKycDocumentDisplayUrl({
        customerId: info.customerId as string,
        documentType: 'BANK_ACCOUNT_PROOF',
        objectKey,
      });
      if (!cancelled && displayUrl) {
        if (!ocrObjectKey) {
          setOcrObjectKey(objectKey);
        }
        setUploadedImageSrc(displayUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    info?.customerId,
    info?.bankDetails,
    formData?.accountProofDocumentUrl,
    ocrObjectKey,
    uploadedImageSrc,
  ]);

  useEffect(() => {
    if (selectedAccountType) {
      setAccountTypeError('');
    }
  }, [selectedAccountType]);

  // Always rebuild GST display from server gstDetails on resume — local
  // hasDeletedGstCertificate must not hide a server-verified certificate.
  useEffect(() => {
    const gstDetails = info?.gstDetails;
    const gstDocKey = gstDetails?.gstDocumentKey || '';
    const serverGstNumber = String(gstDetails?.gstNumber || '');

    if (!gstDocKey && !serverGstNumber) {
      return;
    }

    setHasDeletedGstCertificate(false);

    if (gstDocKey) {
      setGstCertificateObjectKey(gstDocKey);
      setGstCertificateFileName(gstDocKey.split('/').pop() || '');
    }

    if (serverGstNumber) {
      setGstNumber(serverGstNumber);
      setGstFirmName(String(gstDetails?.registeredBusinessName || ''));
      setIsGstVerified(true);
      setGstValidationMessage(
        'Your firm name on GST matches your bank account name. Click Next to continue.'
      );
    }

    if (!gstDocKey || !info?.customerId) {
      return;
    }

    let cancelled = false;
    (async () => {
      const displayUrl = await resolveKycDocumentDisplayUrl({
        customerId: info.customerId as string,
        documentType: 'GST_CERTIFICATE_PROOF',
        objectKey: gstDocKey,
      });
      if (!cancelled && displayUrl) {
        setGstCertificatePublicUrl(displayUrl);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [info?.gstDetails, info?.customerId]);

  const triggerGstOcr = async (objectKey: string) => {
    const response = await axiosInstance.post(
      getUrl(APIResource.gstCertificateOcr, undefined, [
        {
          paramName: 'documentKey',
          paramValue: objectKey,
        },
      ])
    );

    return (response.data?.data || response.data) as GstOcrResponse;
  };

  const validateGstDetails = async ({
    customerId,
    gstin,
    objectKey,
  }: {
    customerId: string;
    gstin: string;
    objectKey: string;
  }) => {
    const response = await axiosInstance.post(
      getUrl(APIResource.validateGst, [
        {
          variableName: 'customerId',
          variableValue: customerId,
        },
      ]),
      {
        gstNumber: gstin,
        uploadedDocumentKey: objectKey,
        customerSource: getSource(),
      }
    );

    return (response.data?.data || response.data) as Record<string, unknown>;
  };

  useEffect(() => {
    if (!prefillImageUrl || uploadedImageSrc || isBankVerified || prefillVerificationStartedRef.current) {
      return;
    }

    prefillVerificationStartedRef.current = true;

    const loadAndVerify = async () => {
      try {
        const { imageUrl, objectKey, customerId: associatedCustomerId, accountType: associatedAccountType } = prefillImageUrl;

        // Set image URL directly for display (no CORS issue for <img> tags)
        setUploadedImageSrc(imageUrl);
        setVerifiedAccountHolderName('');
        setIsBankVerified(false);
        setNameMatched(undefined);
        setValidationMessage('');
        setError('');
        setLoaderTitle(t('verifying_bank_details'));
        setLoaderStages([
          { text: t('fetching_bank_details'), status: 'pending' },
          { text: t('verifying_bank_details'), status: 'pending' },
        ]);
        setReasonForMismatch('');
        setCustomReasonForMismatch('');
        setBankUploadFailureCount(0);
        dispatch(setBankResponse({ value: null }));
        dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
        dispatch(setGenericInformation({ bankDetails: null }));
        dispatch(clearBankDetails());

        onPrefillImageConsumed?.();

        // Run verification using the existing object key (skip upload since image is already in S3)
        setShowLoader(true);

        let ocrData: BankOcrResponse;
        try {
          ocrData = await triggerBankOcr(objectKey);
        } catch (ocrError: any) {
          const ocrErrorCode = String(ocrError?.response?.data?.error || ocrError?.response?.data?.errorCode || '').trim().toUpperCase();
          const ocrPartialData = ocrError?.response?.data?.data || ocrError?.response?.data;
          const partialAccountNumber = String(ocrPartialData?.accountNumber || ocrPartialData?.bankAccountNumber || '').trim();

          if (ocrErrorCode === 'KYC-204' && partialAccountNumber) {
            updateStage(0, 'success');
            await wait(VERIFY_STAGE_TRANSITION_DELAY_MS);
            setOcrAccountNumber(partialAccountNumber);
            setOcrObjectKey(objectKey);
            setShowManualIfsc(true);
            setShowLoader(false);
            setError("We couldn't read the IFSC code from your cheque. Please enter it manually to continue.");
            return;
          }
          throw ocrError;
        }

        updateStage(0, 'success');
        await wait(VERIFY_STAGE_TRANSITION_DELAY_MS);

        // Check if OCR returned account number but no IFSC code
        const ocrRecord = ocrData as Record<string, unknown>;
        const extractedAccountNumber = String(ocrRecord?.accountNumber || ocrRecord?.bankAccountNumber || '').trim();
        const extractedIfscCode = String(ocrRecord?.ifscCode || '').trim();

        if (extractedAccountNumber && !extractedIfscCode) {
          setOcrAccountNumber(extractedAccountNumber);
          setOcrObjectKey(objectKey);
          setShowManualIfsc(true);
          setShowLoader(false);
          setError("We couldn't read the IFSC code from your cheque. Please enter it manually to continue.");
          return;
        }

        const currentCustomerId = info?.customerId;
        if (!currentCustomerId) {
          handleVerificationError(t('customer_details_missing'));
          setTimeout(() => setShowLoader(false), 1200);
          return;
        }

        const bankValidationAction = await dispatch(
          ValidateBank({
            data: {
              customerId: currentCustomerId,
              bankAccountNumber: ocrRecord?.accountNumber || '123456',
              ifscCode: (ocrRecord?.ifscCode || '') as string,
              userProvidedName: panValidation?.panResponse?.fullName || info?.panDetails?.fullName as string || info?.ownerName || info?.shopName || '',
              accountType: associatedAccountType || selectedAccountType,
              customerSource: getSource(),
              accountProofDocumentUrl: objectKey,
            } as any,
          }) as any
        );

        if (!ValidateBank.fulfilled.match(bankValidationAction)) {
          throw getValidateBankFailure(
            bankValidationAction?.payload || bankValidationAction?.error?.message
          );
        }

        const bankValidationResult = bankValidationAction.payload as IValidateBankResult;

        if (!bankValidationResult?.isSuccess) {
          throw getValidateBankFailure(bankValidationResult?.failure || bankValidationResult?.failureMessage);
        }

        await wait(MIN_VERIFY_STAGE_VISIBLE_MS);

        const mergedData = {
          ...(ocrData as Record<string, unknown>),
          ...(bankValidationResult?.bankResponse || {}),
        };

        // const resolvedIfsc = String(mergedData.ifscCode || '');
        // if (resolvedIfsc) {
        //   dispatch(ValidateIFSC({ IFSC: resolvedIfsc }) as any);
        // }

        const verifiedName = extractString(mergedData as Record<string, unknown>, ['accountHolderName', 'name', 'fullName', 'beneficiaryName']) || info?.ownerName || info?.shopName || '';

        setVerifiedAccountHolderName(verifiedName);
        setValidationMessage(t('bank_verified'));
        setNameMatched(parseNameMatched((bankValidationResult?.bankResponse as Record<string, unknown> | null)?.nameMatched));
        previousStateRef.current = null;

        updateStage(1, 'success');

        await wait(SUCCESS_TICK_VISIBLE_MS);
        setShowLoader(false);
        setIsBankVerified(true);
      } catch (err: unknown) {
        const failure = getValidateBankFailure(err);
        // const normalizedMessage = normalizeBankErrorMessage(failure.message);
        const normalizedMessage = resolveKycErrorMessage(failure.errorCode, 'CHEQUE');
        handleVerificationError(normalizedMessage);
        setTimeout(() => {
          setShowLoader(false);
          previousStateRef.current = null;
        }, 1200);
      }
    };

    loadAndVerify();
  }, [prefillImageUrl]); // eslint-disable-line

  const handleReasonModalClose = () => {
    setReasonModalOpen(false);
  };

  const handleReasonSelect = (reason: string, customReason?: string) => {
    setReasonForMismatch(reason);
    setCustomReasonForMismatch(reason === 'Others' ? (customReason || '') : '');
    setReasonModalOpen(false);
  };

  const handleGstFileSelect = () => {
    gstFileInputRef.current?.click();
  };

  const handleGstFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';

    const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    const allowedExtensions = ['jpg', 'jpeg', 'png', 'pdf'];

    if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(extension)) {
      setGstCertificateError('Invalid file type. Please upload a JPG or PDF file.');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setGstCertificateError('File is too large. Maximum size is 5MB.');
      return;
    }

    setGstCertificateError('');
    setGstCertificateFile(file);
    setGstCertificateUploading(true);
    setIsGstVerifying(false);
    setIsGstVerified(false);
    setGstValidationMessage('');
    setGstValidationMismatchCode('');
    setGstNumber('');
    setGstFirmName('');

    try {
      const customerId = info?.customerId;
      if (!customerId) {
        setGstCertificateError('Customer details missing.');
        setGstCertificateUploading(false);
        return;
      }

      const fileExtension = file.name.split('.').pop() || '';
      const fileName = file.name.replace(/\.[^/.]+$/, '').trim().replace(/[^a-zA-Z0-9-_\s]/g, '').replace(/\s+/g, '_') || `GST_${Date.now()}`;

      const response = await axiosInstance.post(
        getUrl(APIResource.uploadCancelledCheque, [
          { variableName: 'customerId', variableValue: customerId },
        ]),
        {
          documentType: 'GST_CERTIFICATE_PROOF',
          fileExtension,
          fileName,
        },
        { params: { source: getSource() } }
      );

      const { presignedUrl, objectKey } = response.data as PresignedUrlResponse;

      await axios.put(presignedUrl, file, {
        headers: { 'Content-Type': file.type },
      });

      // Read file as data URL for reliable viewing across navigation
      const dataUrl = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.readAsDataURL(file);
      });

      setHasDeletedGstCertificate(false);
      setGstCertificateObjectKey(objectKey);
      setGstCertificateFileName(file.name);
      setGstCertificatePublicUrl(dataUrl);
      setGstCertificateUploading(false);
      setIsGstVerifying(true);

      let gstOcrData: GstOcrResponse;
      try {
        gstOcrData = await triggerGstOcr(objectKey);
      } catch (ocrErr: any) {
        const errorCode = String(ocrErr?.response?.data?.errorCode || ocrErr?.response?.data?.error || '').trim().toUpperCase();
        setGstCertificateError(resolveKycErrorMessage(errorCode, 'CHEQUE'));
        setIsGstVerifying(false);
        setGstCertificateUploading(false);
        setGstCertificateObjectKey('');
        setGstCertificateFileName('');
        setGstCertificatePublicUrl('');
        setGstCertificateFile(null);
        return;
      }

      const ocrRecord = gstOcrData as Record<string, unknown>;
      const extractedGstNumber = extractString(ocrRecord, ['gstNumber', 'gstin', 'gstNo', 'gst'])
        .toUpperCase()
        .replace(/\s+/g, '');
        
      const extractedGstFirmName = extractString(ocrRecord, ['registeredBusinessName', 'firmName', 'legalName', 'tradeName', 'businessName', 'name']);

      const handleGstNameMismatch = (errorCode: string, validationData?: Record<string, unknown>) => {
        if (errorCode !== 'KYC-706' && errorCode !== 'KYC-707') {
          return false;
        }

        setGstNumber(extractString(validationData || {}, ['gstNumber', 'gstin']) || extractedGstNumber);
        setGstFirmName(extractString(validationData || {}, ['registeredBusinessName', 'firmName', 'gstName', 'legalName', 'tradeName']) || extractedGstFirmName);
        setGstValidationMismatchCode(errorCode);
        setIsGstVerified(true);
        setGstCertificateError('');
        setIsGstVerifying(false);
        return true;
      };
      
      if (!extractedGstNumber) {
        setGstCertificateError('Unable to read GST details from the uploaded certificate. Please upload a clearer file.');
        setIsGstVerifying(false);
        setGstCertificateUploading(false);
        setGstCertificateObjectKey('');
        setGstCertificateFileName('');
        setGstCertificatePublicUrl('');
        setGstCertificateFile(null);
        return;
      }

      const userPanNumber = String(
        panNumber ||
        (panValidation?.panResponse?.panNumber as string | undefined) ||
        (info?.panDetails?.panNumber as string | undefined) ||
        ''
      ).trim().toUpperCase();
      const extractedGstPan = extractedGstNumber.slice(2, 12).toUpperCase();

      if (!userPanNumber || extractedGstPan !== userPanNumber) {
        setGstNumber(extractedGstNumber);
        setGstFirmName(extractedGstFirmName);
        setGstValidationMismatchCode('KYC-706');
        setIsGstVerified(false);
        setGstValidationMessage('');
        // setGstCertificateError('Invalid GST Certificate. Please upload a valid one.');
        setIsGstVerifying(false);
        setGstCertificateUploading(false);
        return;
      }

      try {
        const gstValidation = await validateGstDetails({
          customerId,
          gstin: extractedGstNumber,
          objectKey,
        });

        const statusCode = Number(gstValidation?.statusCode || 200);
        const gstStatus = extractString(gstValidation, ['gstStatus', 'status']).toUpperCase();
        const isGstActive = gstStatus === 'ACTIVE';

        if (statusCode !== 200 && !isGstActive) {
          const validationErrorCode = String(gstValidation?.errorCode || gstValidation?.error || '').trim().toUpperCase();
          if (handleGstNameMismatch(validationErrorCode, gstValidation)) {
            return;
          }
          setGstCertificateError(resolveKycErrorMessage(validationErrorCode, 'CHEQUE'));
          setIsGstVerifying(false);
          setGstCertificateUploading(false);
          setGstCertificateObjectKey('');
          setGstCertificateFileName('');
          setGstCertificatePublicUrl('');
          setGstCertificateFile(null);
          return;
        }

        const normalizedGstNumber = extractString(gstValidation, ['gstNumber', 'gstin']) || extractedGstNumber;
        const normalizedGstFirmName = extractString(gstValidation, ['registeredBusinessName', 'firmName', 'gstName', 'legalName', 'tradeName']) || extractedGstFirmName;

        setGstNumber(normalizedGstNumber);
        setGstFirmName(normalizedGstFirmName);
        setIsGstVerified(isGstActive || statusCode === 200);
        setGstValidationMessage('Your firm name on GST matches your bank account name. Click Next to continue.');
        setGstCertificateError('');
        setIsGstVerifying(false);
      } catch (validationErr: any) {
        const errorCode = String(validationErr?.response?.data?.errorCode || validationErr?.response?.data?.error || '').trim().toUpperCase();
        const validationData = validationErr?.response?.data?.data || validationErr?.response?.data;
        if (handleGstNameMismatch(errorCode, validationData)) {
          return;
        }
        setGstCertificateError(resolveKycErrorMessage(errorCode, 'CHEQUE'));
        setIsGstVerifying(false);
        setGstCertificateObjectKey('');
        setGstCertificateFileName('');
        setGstCertificatePublicUrl('');
        setGstCertificateFile(null);
      }

      setGstCertificateUploading(false);
    } catch (err) {
      setGstCertificateError('Failed to upload GST certificate. Please try again.');
      setIsGstVerifying(false);
      setGstCertificateUploading(false);
      setGstCertificateFile(null);
      setGstCertificateObjectKey('');
      setGstCertificateFileName('');
      setGstCertificatePublicUrl('');
    }
  };

  const handleGstCertificateDelete = () => {
    // Only for a local pick that has not been verified on the server yet.
    setGstCertificateFile(null);
    setGstCertificateObjectKey('');
    setGstCertificateFileName('');
    setGstCertificatePublicUrl('');
    setGstCertificateError('');
    setGstNumber('');
    setGstFirmName('');
    setIsGstVerifying(false);
    setIsGstVerified(false);
    setGstValidationMessage('');
    setGstValidationMismatchCode('');
    setHasDeletedGstCertificate(true);
  };

  /** Server-verified GST cannot be unlinked — reopen the picker to replace. */
  const handleGstCertificateReplace = () => {
    handleGstFileSelect();
  };

  const handleGstCertificateView = async () => {
    if (
      !gstCertificatePublicUrl &&
      gstCertificateObjectKey &&
      info?.customerId
    ) {
      const displayUrl = await resolveKycDocumentDisplayUrl({
        customerId: info.customerId,
        documentType: 'GST_CERTIFICATE_PROOF',
        objectKey: gstCertificateObjectKey,
      });
      if (displayUrl) {
        setGstCertificatePublicUrl(displayUrl);
      }
    }
    setGstDocumentViewerOpen(true);
  };

  const handleBankPreviewError = async () => {
    if (bankImageRetryUsedRef.current) return;
    if (!isRemoteDocumentDisplayUrl(uploadedImageSrc)) return;
    const objectKey =
      ocrObjectKey ||
      formData?.accountProofDocumentUrl ||
      prefillImageUrl?.objectKey ||
      '';
    if (!objectKey || !info?.customerId) return;
    bankImageRetryUsedRef.current = true;
    const refreshed = await fetchKycDocumentPresignedUrl({
      customerId: prefillImageUrl?.customerId || info.customerId,
      documentType: 'BANK_ACCOUNT_PROOF',
      objectKey,
      force: true,
    }).catch(() => null);
    if (refreshed) {
      setUploadedImageSrc(refreshed);
    }
  };

  const isGstPdf = gstCertificateFileName.toLowerCase().endsWith('.pdf');

  const resetGstStateForNewCheque = () => {
    setGstCertificateFile(null);
    setGstCertificateObjectKey('');
    setGstCertificateFileName('');
    setGstCertificatePublicUrl('');
    setGstCertificateUploading(false);
    setIsGstVerifying(false);
    setGstCertificateError('');
    setGstNumber('');
    setGstFirmName('');
    setIsGstVerified(false);
    setGstValidationMessage('');
    setGstValidationMismatchCode('');
    setHasDeletedGstCertificate(true);
    setGstDocumentViewerOpen(false);
  };

  const updateStage = (index: number, status: LoaderStageStatus) => {
    setLoaderStages((prev) =>
      prev.map((stage, stageIndex) =>
        stageIndex === index ? { ...stage, status } : stage
      )
    );
  };

  const getPreferredFileName = (file: File) => {
    const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, '').trim();
    const safeFileName = fileNameWithoutExtension
      .replace(/[^a-zA-Z0-9-_\s]/g, '')
      .replace(/\s+/g, '_');

    return safeFileName || `BANK_${Date.now()}`;
  };

  const getBankUploadUrl = async ({
    customerId,
    file,
  }: {
    customerId: string;
    file: File;
  }) => {
    const fileExtension = file.name.split('.').pop() || '';
    const fileName = getPreferredFileName(file);

    const response = await axiosInstance.post(
      getUrl(APIResource.uploadCancelledCheque, [
        {
          variableName: 'customerId',
          variableValue: customerId,
        },
      ]),
      {
        documentType: 'BANK_ACCOUNT_PROOF',
        fileExtension,
        fileName,
      },
      {
        params: { source: getSource() },
      }
    );

    return response.data as PresignedUrlResponse;
  };

  const uploadToS3 = async (uploadUrl: string, file: File) => {
    await axios.put(uploadUrl, file, {
      headers: {
        'Content-Type': file.type,
      },
    });
  };

  const triggerBankOcr = async (objectKey: string) => {
    const response = await axiosInstance.post(
      getUrl(APIResource.bankAccountOcr, undefined, [
        {
          paramName: 'documentKey',
          paramValue: objectKey,
        },
      ])
    );

    return (response.data?.data || response.data) as BankOcrResponse;
  };

  const handleModalClose = () => {
    setModalOpen(false);
    // Discard the captured image — user chose not to verify
    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
      lastObjectUrlRef.current = null;
    }
    setUploadedImageSrc(null);
    setSelectedFile(null);
  };

  const openTipsForAction = (action: 'camera' | 'upload') => {
    setTipsAction(action);
    setTipsModalOpen(true);
  };

  const proceedOpenCamera = () => {
    pendingGalleryVerifyRef.current = false;
    webcam.handleOpenCamera();
  };

  const proceedOpenGallery = () => {
    pendingGalleryVerifyRef.current = true;
    galleryInputRef.current?.click();
  };

  const handleOpenCamera = () => {
    if (!uploadedImageSrc) {
      openTipsForAction('camera');
      return;
    }

    proceedOpenCamera();
  };

  const handleOpenGallery = () => {
    if (!uploadedImageSrc) {
      openTipsForAction('upload');
      return;
    }

    proceedOpenGallery();
  };

  const handleTipsGotIt = () => {
    setTipsModalOpen(false);
    if (tipsAction === 'camera') {
      proceedOpenCamera();
      return;
    }

    proceedOpenGallery();
  };

  const handleFileSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) { return; }

    if (!isAllowedImageFile(file)) {
      pendingGalleryVerifyRef.current = false;
      setError(invalidFileTypeMessage);
      event.target.value = '';
      return;
    }

    if (lastObjectUrlRef.current) { URL.revokeObjectURL(lastObjectUrlRef.current); }

    previousStateRef.current = {
      imageSrc: uploadedImageSrc,
      isBankVerified,
      verifiedAccountHolderName,
      validationMessage,
      bankResponse: bankSlice?.bankResponse,
      bankStatus: bankSlice?.bankStatus,
      bankDetails: info?.bankDetails,
      ifscDetails: ifscSlice?.bankDetails,
    };
    const objectUrl = URL.createObjectURL(file);
    lastObjectUrlRef.current = objectUrl;

    setSelectedFile(file);
    setUploadedImageSrc(objectUrl);
    setVerifiedAccountHolderName('');
    setIsBankVerified(false);
    setNameMatched(undefined);
    setValidationMessage('');
    setError('');
    setLoaderTitle(t('verifying_bank_details'));
    setLoaderStages([
      { text: t('fetching_bank_details'), status: 'pending' },
      { text: t('verifying_bank_details'), status: 'pending' },
    ]);
    setReasonForMismatch('');
    setCustomReasonForMismatch('');
    setBankUploadFailureCount(0);
    setShowManualIfsc(false);
    setManualIfscInput('');
    setIfscValidationStatus('idle');
    setIfscBankName('');
    setIfscBranchName('');
    setIfscError('');
    setManualIfscBankError('');
    setOcrAccountNumber('');
    setOcrObjectKey('');
    resetGstStateForNewCheque();
    dispatch(setBankResponse({ value: null }));
    dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
    dispatch(setGenericInformation({ bankDetails: null }));
    dispatch(clearBankDetails());

    if (pendingGalleryVerifyRef.current) {
      // useEffect will trigger verification once state updates land
    } else {
      setModalOpen(true);
    }

    event.target.value = '';
  };

  const handleDeleteUploaded = (clearStatusMessage = true) => {
    if (isBankVerified) {
      setBankUploadFailureCount(0);
    }

    if (lastObjectUrlRef.current) {
      URL.revokeObjectURL(lastObjectUrlRef.current);
      lastObjectUrlRef.current = null;
    }

    setUploadedImageSrc(null);
    setSelectedFile(null);
    setVerifiedAccountHolderName('');
    setIsBankVerified(false);
    setNameMatched(undefined);
    setValidationMessage('');
    if (clearStatusMessage) {
      setError('');
    }
    setLoaderTitle(t('verifying_bank_details'));
    setLoaderStages([
      { text: t('fetching_bank_details'), status: 'pending' },
      { text: t('verifying_bank_details'), status: 'pending' },
    ]);
    setReasonForMismatch('');
    setCustomReasonForMismatch('');
    setShowManualIfsc(false);
    setManualIfscInput('');
    setIfscValidationStatus('idle');
    setIfscBankName('');
    setIfscBranchName('');
    setIfscError('');
    setManualIfscBankError('');
    setOcrAccountNumber('');
    setOcrObjectKey('');
    resetGstStateForNewCheque();
    dispatch(setBankResponse({ value: null }));
    dispatch(setBankStatus({ value: BANK_STATUS_VARIANTS.PENDING }));
    dispatch(setGenericInformation({ bankDetails: null }));
    dispatch(clearBankDetails());
  };

  const handleEditClick = () => {
    setDeleteModalOpen(true);
  };

  const handleEditCancel = () => {
    setDeleteModalOpen(false);
  };

  const handleEditUploadPhoto = () => {
    setDeleteModalOpen(false);
    proceedOpenGallery();
  };

  const handleEditTakePhoto = () => {
    setDeleteModalOpen(false);
    proceedOpenCamera();
  };

  const handleVerificationError = (message: string) => {
    // const mappedMessage = normalizeBankErrorMessage(message);
    // setError(mappedMessage);
    // setIsBankVerified(false);
    // setValidationMessage(mappedMessage);
    setError(message);
    setIsBankVerified(false);
    setNameMatched(undefined);
    setValidationMessage(message);
    if (uploadedImageSrcRef.current) {
      setBankUploadFailureCount((prev) => prev + 1);
    }

    setLoaderStages((prev) => {
      const firstPendingIndex = prev.findIndex((stage) => stage.status === 'pending');
      if (firstPendingIndex === -1) {
        return prev;
      }

      return prev.map((stage, index) =>
        index === firstPendingIndex ? { ...stage, status: 'error' } : stage
      );
    });
  };

  const handleVerify = async () => {
    if (!uploadedImageSrc || !selectedFile) return;

    const customerId = info?.customerId;
    if (!customerId) {
      setError(t('customer_details_missing'));
      return;
    }

    setModalOpen(false);
    setError('');
    setLoaderTitle(t('verifying_bank_details'));
    setLoaderStages([
      { text: t('fetching_bank_details'), status: 'pending' },
      { text: t('verifying_bank_details'), status: 'pending' },
    ]);
    setShowLoader(true);

    const presignedUrlResponse = await getBankUploadUrl({
      customerId,
      file: selectedFile,
    });

    await uploadToS3(presignedUrlResponse.presignedUrl, selectedFile);

    let ocrData: BankOcrResponse;
    try {
      ocrData = await triggerBankOcr(presignedUrlResponse.objectKey);
    } catch (ocrError: any) {
      const ocrErrorCode = String(ocrError?.response?.data?.error || ocrError?.response?.data?.errorCode || '').trim().toUpperCase();
      const ocrPartialData = ocrError?.response?.data?.data || ocrError?.response?.data;
      const partialAccountNumber = String(ocrPartialData?.accountNumber || ocrPartialData?.bankAccountNumber || '').trim();

      if (ocrErrorCode === 'KYC-204' && partialAccountNumber) {
        // IFSC couldn't be read but account number was found - show manual IFSC entry
        updateStage(0, 'success');
        await wait(VERIFY_STAGE_TRANSITION_DELAY_MS);
        setOcrAccountNumber(partialAccountNumber);
        setOcrObjectKey(presignedUrlResponse.objectKey);
        setShowManualIfsc(true);
        setShowLoader(false);
        setError("We couldn't read the IFSC code from your cheque. Please enter it manually to continue.");
        return;
      }
      throw ocrError;
    }

    updateStage(0, 'success');

    await wait(VERIFY_STAGE_TRANSITION_DELAY_MS);

    // Check if OCR returned account number but no IFSC code
    const ocrRecord = ocrData as Record<string, unknown>;
    const extractedAccountNumber = String(ocrRecord?.accountNumber || ocrRecord?.bankAccountNumber || '').trim();
    const extractedIfscCode = String(ocrRecord?.ifscCode || '').trim();

    if (extractedAccountNumber && !extractedIfscCode) {
      // IFSC not readable from cheque - show manual entry
      setOcrAccountNumber(extractedAccountNumber);
      setOcrObjectKey(presignedUrlResponse.objectKey);
      setShowManualIfsc(true);
      setShowLoader(false);
      setError("We couldn't read the IFSC code from your cheque. Please enter it manually to continue.");
      return;
    }

    // Build payload and dispatch ValidateBank directly
    const bankValidationAction = await dispatch(
      ValidateBank({
        data: {
          customerId,
          bankAccountNumber: ocrRecord?.accountNumber || '123456',
          ifscCode: (ocrRecord?.ifscCode || '') as string,
          userProvidedName: panValidation?.panResponse?.fullName || info?.panDetails?.fullName as string || info?.ownerName || info?.shopName || '',
          accountType: selectedAccountType,
          customerSource: getSource(),
          accountProofDocumentUrl: presignedUrlResponse.objectKey,
        } as any,
      }) as any
    );

    if (!ValidateBank.fulfilled.match(bankValidationAction)) {
      throw getValidateBankFailure(
        bankValidationAction?.payload || bankValidationAction?.error?.message
      );
    }

    const bankValidationResult = bankValidationAction.payload as IValidateBankResult;

    // Handle failure case
    if (!bankValidationResult?.isSuccess) {
      throw getValidateBankFailure(bankValidationResult?.failure || bankValidationResult?.failureMessage);
    }

    // Handle success case
    await wait(MIN_VERIFY_STAGE_VISIBLE_MS);

    const mergedData = {
      ...(ocrData as Record<string, unknown>),
      ...(bankValidationResult?.bankResponse || {}),
    };

    const verifiedName = extractString(mergedData, ['accountHolderName','name','fullName','beneficiaryName']) || info?.ownerName || info?.shopName || '';

    setVerifiedAccountHolderName(verifiedName);
    setNameMatched(parseNameMatched((bankValidationResult?.bankResponse as Record<string, unknown> | null)?.nameMatched));
    setValidationMessage(t('bank_verified'));
    previousStateRef.current = null;

    updateStage(1, 'success');

    await wait(SUCCESS_TICK_VISIBLE_MS);
    setShowLoader(false);
    setIsBankVerified(true);

  };

  const handleVerifyWrapper = async () => {
    try {
      await handleVerify();
    } catch (err: unknown) {
      const failure = getValidateBankFailure(err);
      // const normalizedMessage = normalizeBankErrorMessage(failure.message);
      const normalizedMessage = resolveKycErrorMessage(failure.errorCode, 'CHEQUE');
      handleVerificationError(normalizedMessage);

      setTimeout(() => {
        setShowLoader(false);
        previousStateRef.current = null;
      }, 1200);
    }
  };

  const handleConfirmIfsc = async () => {
    const ifscCode = manualIfscInput.trim().toUpperCase();
    if (!ifscCode) return;

    if (!KYCRegxConstants.isfcRegx.test(ifscCode)) {
      setIfscValidationStatus('invalid');
      setIfscError('Invalid IFSC Code');
      setIfscBankName('');
      setIfscBranchName('');
      return;
    }

    setIfscValidationStatus('loading');
    setIfscError('');
    setIfscBankName('');
    setIfscBranchName('');

    try {
      const response = await axiosInstance(getUrl(APIResource.IFSC, undefined, [
        { paramName: 'ifscCode', paramValue: ifscCode },
      ]));
      if (response?.data?.error) {
        setIfscValidationStatus('invalid');
        setIfscError('Invalid IFSC Code');
      } else {
        const bankName = response?.data?.bank || response?.data?.bankName || '';
        const branchName = response?.data?.branch || response?.data?.branchName || '';
        setIfscValidationStatus('valid');
        setIfscBankName(bankName);
        setIfscBranchName(branchName);
        setIfscError('');
        await handleVerifyWithManualIfsc();
      }
    } catch (err: any) {
      setIfscValidationStatus('invalid');
      setIfscBankName('');
      setIfscBranchName('');
      if (err?.response?.status === 404) {
        setIfscError('Invalid IFSC Code');
      } else {
        setIfscError('Invalid IFSC Code');
      }
    }
  };

  const handleVerifyWithManualIfsc = async () => {
    if (!ocrAccountNumber || !ocrObjectKey) return;

    const customerId = info?.customerId;
    if (!customerId) {
      setError(t('customer_details_missing'));
      return;
    }

    setError('');
    setManualIfscBankError('');
    setLoaderTitle(t('verifying_bank_details'));
    setLoaderStages([
      { text: t('verifying_bank_details'), status: 'pending' },
    ]);
    setShowLoader(true);

    try {
      const bankValidationAction = await dispatch(
        ValidateBank({
          data: {
            customerId,
            bankAccountNumber: ocrAccountNumber,
            ifscCode: manualIfscInput.trim().toUpperCase(),
            userProvidedName: panValidation?.panResponse?.fullName || info?.panDetails?.fullName as string || info?.ownerName || info?.shopName || '',
            accountType: selectedAccountType,
            customerSource: getSource(),
            accountProofDocumentUrl: ocrObjectKey,
          } as any,
        }) as any
      );

      if (!ValidateBank.fulfilled.match(bankValidationAction)) {
        throw getValidateBankFailure(
          bankValidationAction?.payload || bankValidationAction?.error?.message
        );
      }

      const bankValidationResult = bankValidationAction.payload as IValidateBankResult;

      if (!bankValidationResult?.isSuccess) {
        throw getValidateBankFailure(bankValidationResult?.failure || bankValidationResult?.failureMessage);
      }

      await wait(MIN_VERIFY_STAGE_VISIBLE_MS);

      const mergedData = {
        ...(bankValidationResult?.bankResponse || {}),
      };

      const verifiedName = extractString(mergedData as Record<string, unknown>, ['accountHolderName', 'name', 'fullName', 'beneficiaryName']) || info?.ownerName || info?.shopName || '';

      setVerifiedAccountHolderName(verifiedName);
      setNameMatched(parseNameMatched((bankValidationResult?.bankResponse as Record<string, unknown> | null)?.nameMatched));
      setValidationMessage(t('bank_verified'));
      setError('');
      setManualIfscBankError('');
      previousStateRef.current = null;

      updateStage(0, 'success');
      await wait(SUCCESS_TICK_VISIBLE_MS);
      setShowLoader(false);
      setIsBankVerified(true);

    } catch (err: unknown) {
      const failure = getValidateBankFailure(err);
      const normalizedMessage = resolveKycErrorMessage(failure.errorCode, 'CHEQUE');
      console.error('Bank verification failed with manual IFSC:', err, normalizedMessage);
      setManualIfscBankError(normalizedMessage);
      setTimeout(() => {
        setShowLoader(false);
        previousStateRef.current = null;
      }, 1200);
    }
  };

  const panNameForComparison = String( (panValidation?.panResponse?.fullName as string | undefined) || (info?.panDetails?.fullName as string | undefined) || '' );
  const panNumberForComparison = String(
    panNumber ||
    (panValidation?.panResponse?.panNumber as string | undefined) ||
    (info?.panDetails?.panNumber as string | undefined) ||
    ''
  ).trim().toUpperCase();
  const firmNameForComparison = String(formData?.registeredBusinessName || info?.shopName || '');
  const chequeNameForComparison = String(verifiedAccountHolderName || '');
  const establishmentTypeEnum = resolveEstablishmentType(normalizedEstablishmentType);
  const gstPanForComparison = gstNumber.replace(/\s+/g, '').slice(2, 12).toUpperCase();
  const isGstPanMismatch = Boolean(
    gstValidationMismatchCode === 'KYC-706' ||
    (gstCertificateObjectKey &&
      panNumberForComparison &&
      gstPanForComparison &&
      gstPanForComparison !== panNumberForComparison)
  );
  const isGstFirmNameMismatch = Boolean(
    gstValidationMismatchCode === 'KYC-707' ||
    (gstCertificateObjectKey &&
      gstFirmName &&
      chequeNameForComparison &&
      !isNameMatch(gstFirmName, chequeNameForComparison))
  );
  const hasGstNameMismatch = isGstPanMismatch || isGstFirmNameMismatch;
  const isGstVerificationValid = isGstVerified && !hasGstNameMismatch;
  
  const showChequeNameMismatchAlert = isBankVerified && nameMatched === false;
  const normalizedMismatchReason = reasonForMismatch === 'Others' ? customReasonForMismatch.trim() : reasonForMismatch.trim();
  const isGstSectionEnabled = KYCConstants.ENABLE_GST_SECTION;
  const isNameMismatchBlocked = showChequeNameMismatchAlert && isGstSectionEnabled && (!isGstVerified || hasGstNameMismatch);
  const isGstCertificateProvided = !isGstSectionEnabled || !showChequeNameMismatchAlert || Boolean(gstCertificateObjectKey);
  const isGstValidationSatisfied = !isGstSectionEnabled || !showChequeNameMismatchAlert || !gstCertificateObjectKey || (isGstVerified && !hasGstNameMismatch);
  const isMismatchReasonProvided = !showChequeNameMismatchAlert || Boolean(normalizedMismatchReason) || Boolean(gstCertificateObjectKey);

  const isNextEnabled = Boolean(uploadedImageSrc && isBankVerified && !error && !isNameMismatchBlocked && isMismatchReasonProvided && isGstCertificateProvided && isGstValidationSatisfied);
  // const isManualIfscVerifyEnabled = showManualIfsc && ifscValidationStatus === 'valid' && !isBankVerified;
  const activePendingIndex = loaderStages.findIndex((stage) => stage.status === 'pending');
  const inlineStatusMessage = (() => {
    if (activePendingIndex >= 0) {
      return loaderStages[activePendingIndex].text;
    }

    const errorStage = loaderStages.find((stage) => stage.status === 'error');
    if (errorStage) {
      return errorStage.text;
    }

    return loaderTitle;
  })();

  const primaryCtaLabel = isBankVerified ? t('next') : t('next');
  const isPrimaryEnabled = isNextEnabled;

  const handleNext = async () => {
    if (!isBankVerified) {
      return;
    }

    if (showChequeNameMismatchAlert && !gstCertificateObjectKey && !normalizedMismatchReason) {
      setReasonModalOpen(true);
      return;
    }

    if (!selectedAccountType) {
      setAccountTypeError('Please select account type to proceed.');
      return;
    }

    const nextData: IBankDetailsForm = {
      ...BankDetailsFormDefaultValues,
      ...formData,
      accType: selectedAccountType,
      bankAccountNumber: formData?.bankAccountNumber || '',
      reEnterBankAccountNumber: formData?.reEnterBankAccountNumber || formData?.bankAccountNumber || '',
      ifscCode: formData?.ifscCode || ifscSlice?.bankDetails || null,
      cancelledCheque: formData?.cancelledCheque || null,
      accountProofDocumentUrl: formData?.accountProofDocumentUrl,
      nameMismatchReason: normalizedMismatchReason,
      gstCertificateDocumentUrl: gstCertificateObjectKey || undefined,
      gstCertificatePublicUrl: gstCertificatePublicUrl || undefined,
      gstNumber: gstNumber || undefined,
      gstFirmName: gstFirmName || undefined,
      isGstVerified,
    };

    nextStep?.(nextData);
  };

  return (
    <Box className="bank-details-ocr-root" data-testid="bank-details-ocr-root" sx={bankDetailsOCRStyles.root}>
      <Box
        ref={contentContainerRef}
        className="bank-details-ocr-content"
        data-testid="bank-details-ocr-content"
        sx={bankDetailsOCRStyles.content}
      >
        <Box
          className="bank-details-ocr-section"
          data-testid="bank-details-ocr-section"
          sx={{
            ...bankDetailsOCRStyles.section,
          }}
        >
          <Box className="bank-details-ocr-header" data-testid="bank-details-ocr-header" sx={bankDetailsOCRStyles.header}>
            <Typography className="bank-details-ocr-step-text" data-testid="bank-details-ocr-step-text" sx={bankDetailsOCRStyles.stepText}>
              {t('upload_bank_step_text')}
            </Typography>
            <Box className="bank-details-ocr-title-row" data-testid="bank-details-ocr-title-row" sx={bankDetailsOCRStyles.titleRow}>
              <Typography className="bank-details-ocr-title-text" data-testid="bank-details-ocr-title-text" sx={bankDetailsOCRStyles.titleText}>
                 {t('upload_cancelled_cheque')}
              </Typography>
               <Typography className="bank-details-ocr-required-mark" data-testid="bank-details-ocr-required-mark" sx={bankDetailsOCRStyles.requiredMark}>
                 *
               </Typography>
             </Box>
            <Typography className="bank-details-ocr-subtitle-text" data-testid="bank-details-ocr-subtitle-text" sx={bankDetailsOCRStyles.subtitleText}>
              {t('upload_cancelled_cheque_subtitle')}
            </Typography>
          </Box>

          {uploadedImageSrc ? (
            <Box
              className="bank-details-ocr-upload-area"
              data-testid="bank-details-ocr-upload-area"
              sx={{
                ...bankDetailsOCRStyles.uploadArea
              }}
            >      
              <Box className="bank-details-ocr-preview-wrap" data-testid="bank-details-ocr-preview-wrap" sx={bankDetailsOCRStyles.previewWrap}>
                <Box
                  component="img"
                  className="bank-details-ocr-preview-image"
                  data-testid="bank-details-ocr-preview-image"
                  src={uploadedImageSrc}
                  alt="Cancelled cheque"
                  onError={handleBankPreviewError}
                  sx={bankDetailsOCRStyles.previewImage}
                />
                <Box
                  className="bank-details-ocr-edit-image-button"
                  data-testid="bank-details-ocr-edit-image-button"
                  onClick={handleEditClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      handleEditClick();
                    }
                  }}
                  sx={bankDetailsOCRStyles.editImageButton}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M1.99915 11.5007V14.0007H4.49915L11.8725 6.62736L9.37248 4.12736L1.99915 11.5007ZM13.8058 4.69402C14.0658 4.43402 14.0658 4.01402 13.8058 3.75402L12.2458 2.19402C11.9858 1.93402 11.5658 1.93402 11.3058 2.19402L10.0858 3.41402L12.5858 5.91402L13.8058 4.69402Z" fill="white"/>
                  </svg>
                  <Typography sx={bankDetailsOCRStyles.editButtonText}>
                    {t('edit', 'Edit')}
                  </Typography>
                </Box>
              </Box>

            </Box>
          ) : (
            <Box className="bank-details-ocr-action-row" data-testid="bank-details-ocr-action-row" sx={{ ...bankDetailsOCRStyles.actionRow, height: '24vh' }}>
              <Paper
                className="bank-details-ocr-camera-card"
                data-testid="bank-details-ocr-camera-card"
                elevation={0}
                onClick={handleOpenCamera}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenCamera();
                  }
                }}
                sx={{ ...bankDetailsOCRStyles.actionItem, ...bankDetailsOCRStyles.cameraActionItem }}
              >
                <Box className="bank-details-ocr-camera-icon-wrap" data-testid="bank-details-ocr-camera-icon-wrap" sx={{ ...bankDetailsOCRStyles.actionIconCircle, ...bankDetailsOCRStyles.cameraActionIconCircle }}>
                  <CameraSVG style={bankDetailsOCRStyles.cameraIcon as React.CSSProperties} />
                </Box>
                <Typography className="bank-details-ocr-camera-upload-text" data-testid="bank-details-ocr-camera-upload-text" sx={bankDetailsOCRStyles.actionTitle}>
                  {t('take_photo')}
                </Typography>
                <Typography className="bank-details-ocr-camera-upload-subtext" data-testid="bank-details-ocr-camera-upload-subtext" sx={bankDetailsOCRStyles.actionSubtitle}>
                  {t('open_camera')}
                </Typography>
              </Paper>

              <Paper
                className="bank-details-ocr-gallery-card"
                data-testid="bank-details-ocr-gallery-card"
                elevation={0}
                onClick={handleOpenGallery}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleOpenGallery();
                  }
                }}
                sx={{ ...bankDetailsOCRStyles.actionItem, ...bankDetailsOCRStyles.galleryActionItem }}
              >
                <Box className="bank-details-ocr-gallery-icon-wrap" data-testid="bank-details-ocr-gallery-icon-wrap" sx={{ ...bankDetailsOCRStyles.actionIconCircle, ...bankDetailsOCRStyles.galleryActionIconCircle }}>
                  <GallerySVG style={bankDetailsOCRStyles.galleryIcon as React.CSSProperties} />
                </Box>
                <Typography className="bank-details-ocr-gallery-upload-text" data-testid="bank-details-ocr-gallery-upload-text" sx={bankDetailsOCRStyles.actionTitle}>
                  {t('upload_photo')}
                </Typography>
                <Typography className="bank-details-ocr-gallery-upload-subtext" data-testid="bank-details-ocr-gallery-upload-subtext" sx={bankDetailsOCRStyles.actionSubtitle}>
                  {t('from_gallery')}
                </Typography>
              </Paper>
            </Box>
          )}

          {uploadedImageSrc && showLoader && (
            <Box className="bank-details-ocr-inline-loader-overlay" data-testid="bank-details-ocr-inline-loader-overlay" sx={bankDetailsOCRStyles.inlineLoaderOverlay}>
              <Box className="bank-details-ocr-inline-loader-icon-wrap" data-testid="bank-details-ocr-inline-loader-icon-wrap" sx={bankDetailsOCRStyles.inlineLoaderIconWrap}>
                <CircularProgress size={22} sx={{ color: '#16A34A' }} />
              </Box>
              <Typography className="bank-details-ocr-inline-loader-status-text" data-testid="bank-details-ocr-inline-loader-status-text" sx={bankDetailsOCRStyles.inlineLoaderStatusText}>
                {inlineStatusMessage}
              </Typography>
            </Box>
          )}

          <input className="bank-details-ocr-gallery-input" data-testid="bank-details-ocr-gallery-input" ref={galleryInputRef} type="file" accept="image/jpeg,image/png,.jpg,.jpeg,.png" onChange={handleFileSelected} style={bankDetailsOCRStyles.hiddenFileInput} />

          {associatedCustomerDataList && associatedCustomerDataList.length > 0 && isBankVerified && (
            <Box
              data-testid="bank-details-ocr-association-banner"
              sx={{
                backgroundColor: '#E8F5F5',
                borderRadius: '8px',
                padding: '12px 16px',
                marginTop: '12px',
              }}
            >
              <Typography sx={{ fontSize: '14px', color: '#333', lineHeight: 1.5 }}>
                {t('bank_details_auto_filled', { defaultValue: 'Bank details auto filled for PAN' })}{' '}
                <Typography component="span" sx={{ fontWeight: 700, fontSize: '14px' }}>
                  {panNumber}
                </Typography>
                .
              </Typography>
              <Typography
                onClick={() => setAssociationDetailsOpen(true)}
                sx={{ color: '#007CBF', fontWeight: 600, fontSize: '14px', cursor: 'pointer', textDecoration: 'underline'}}
              >
                {t('view_details', { defaultValue: 'View Details.' })}
              </Typography>
            </Box>
          )}

          {!showLoader && isBankVerified && validationMessage && !showChequeNameMismatchAlert && !showManualIfsc && (
            <Box className="bank-details-ocr-verified-row" data-testid="bank-details-ocr-verified-row" sx={bankDetailsOCRStyles.verifiedRow}>
              <CheckCircle className="bank-details-ocr-check-icon-large" data-testid="bank-details-ocr-check-icon-large" sx={bankDetailsOCRStyles.checkIconLarge} />
              <Box>
                <Typography className="bank-details-ocr-status-text" data-testid="bank-details-ocr-status-text" sx={bankDetailsOCRStyles.statusText(isBankVerified)}>
                  {validationMessage}
                </Typography>
                {isBankVerified && verifiedAccountHolderName && (
                  <Typography className="bank-details-ocr-verified-name-text" data-testid="bank-details-ocr-verified-name-text" sx={bankDetailsOCRStyles.verifiedNameText}>
                    {verifiedAccountHolderName}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {!showLoader && showChequeNameMismatchAlert && !showManualIfsc && !isGstVerificationValid && !hasGstNameMismatch && (
            <Box className="bank-details-ocr-attempts-warning-row" data-testid="bank-details-ocr-name-mismatch-warning-row" sx={bankDetailsOCRStyles.attemptsWarningRow}>
              <Box className="bank-details-ocr-error-icon-wrap" data-testid="bank-details-ocr-name-mismatch-warning-icon" sx={bankDetailsOCRStyles.errorIconWrap}>
                <ErrorAlert />
              </Box>
              <Typography className="bank-details-ocr-attempts-warning-text" data-testid="bank-details-ocr-name-mismatch-warning-text" sx={bankDetailsOCRStyles.attemptsWarningText}>
                {chequeNameMismatchMessage}{' '}
                <Typography
                  component="span"
                  onClick={() => {
                    setNameMismatchDetailsScenario(establishmentTypeEnum === 'PROPRIETORSHIP' ? 'proprietorship' : 'partnership_company');
                    setNameMismatchDetailsOpen(true);
                  }}
                  sx={bankDetailsOCRStyles.viewDetailsLink}
                >
                {t('view_details')}
                </Typography>
              </Typography>
            </Box>
          )}

          {!showLoader && isGstSectionEnabled && showChequeNameMismatchAlert && !showManualIfsc && (
            <>
              {!isGstVerified && !hasGstNameMismatch && (
              <Box data-testid="gst-info-box" sx={bankDetailsOCRStyles.gstInfoBox}>
                <Typography sx={bankDetailsOCRStyles.gstInfoText}>
                  Upload a cheque with a matching firm name, or verify using your GST certificate
                </Typography>
              </Box>
              )}

              <Box sx={bankDetailsOCRStyles.gstSectionTitle}>
                <Typography sx={bankDetailsOCRStyles.gstSectionTitleText}>
                  Verify with GST Certificate
                </Typography>
                <Typography sx={bankDetailsOCRStyles.gstSectionAsterisk}>*</Typography>
              </Box>

              <Box sx={bankDetailsOCRStyles.gstUploadStatusGroup}>
                {gstCertificateObjectKey && gstCertificateFileName ? (
                  <Box data-testid="gst-uploaded-box" sx={bankDetailsOCRStyles.gstUploadedBox}>
                    <Box sx={bankDetailsOCRStyles.gstUploadedIconWrap}>
                      <img src={isGstPdf ? GstPdfIcon : GstImageIcon} alt="GST Certificate" width="32" height="32" />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={bankDetailsOCRStyles.gstUploadedFileName}>
                        GST Certificate
                      </Typography>
                      <Typography
                        onClick={!(gstCertificateUploading || isGstVerifying) ? handleGstCertificateView : undefined}
                        sx={{
                          ...bankDetailsOCRStyles.gstUploadedViewLink,
                          ...((gstCertificateUploading || isGstVerifying) ? { opacity: 0.5, pointerEvents: 'none' } : {}),
                        }}
                      >
                        View
                      </Typography>
                    </Box>
                    {isGstVerified ? (
                      <Button
                        data-testid="gst-replace-button"
                        onClick={handleGstCertificateReplace}
                        disabled={gstCertificateUploading || isGstVerifying}
                        sx={bankDetailsOCRStyles.gstUploadedDeleteButton}
                      >
                        Replace
                      </Button>
                    ) : (
                      <IconButton
                        data-testid="gst-delete-button"
                        onClick={handleGstCertificateDelete}
                        disabled={gstCertificateUploading || isGstVerifying}
                        sx={bankDetailsOCRStyles.gstUploadedDeleteButton}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                        </svg>
                      </IconButton>
                    )}
                  </Box>
                ) : (
                  <Box
                    data-testid="gst-upload-box"
                    onClick={!(gstCertificateUploading || isGstVerifying) ? handleGstFileSelect : undefined}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => { if (!(gstCertificateUploading || isGstVerifying) && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleGstFileSelect(); } }}
                    sx={{
                      ...bankDetailsOCRStyles.gstUploadBox,
                      ...((gstCertificateUploading || isGstVerifying) ? { opacity: 0.6, pointerEvents: 'none' } : {}),
                    }}
                  >
                    <Box sx={bankDetailsOCRStyles.gstUploadIconWrap}>
                      <GallerySVG />
                    </Box>
                    <Box>
                      <Typography sx={bankDetailsOCRStyles.gstUploadLabel}>Upload GST Certificate</Typography>
                      <Typography sx={bankDetailsOCRStyles.gstUploadSubtext}>JPG, PNG, PDF</Typography>
                    </Box>
                  </Box>
                )}

                {(gstCertificateUploading || isGstVerifying) && (
                  <Box data-testid="gst-verifying-status" sx={bankDetailsOCRStyles.inlineLoaderOverlay}>
                    <Box sx={bankDetailsOCRStyles.inlineLoaderIconWrap}>
                      <CircularProgress size={22} sx={{ color: '#16A34A' }} />
                    </Box>
                    <Typography sx={bankDetailsOCRStyles.inlineLoaderStatusText}>
                      {gstCertificateUploading ? 'Uploading GST certificate...' : 'Verifying GST details...'}
                    </Typography>
                  </Box>
                )}

                {gstCertificateError && (
                  <Typography sx={bankDetailsOCRStyles.gstErrorText}>
                    {gstCertificateError}
                  </Typography>
                )}
              </Box>

              {isGstPanMismatch && (
                <Box data-testid="gst-pan-mismatch-warning" sx={bankDetailsOCRStyles.attemptsWarningRow}>
                  <Box sx={bankDetailsOCRStyles.errorIconWrap}>
                    <ErrorAlert />
                  </Box>
                  <Typography sx={bankDetailsOCRStyles.attemptsWarningText}>
                    PAN number on your GST does not match the uploaded PAN.{' '}
                    <Typography
                      component="span"
                      onClick={() => {
                        setNameMismatchDetailsScenario('gst_pan');
                        setNameMismatchDetailsOpen(true);
                      }}
                      sx={bankDetailsOCRStyles.viewDetailsLink}
                    >
                      {t('view_details')}
                    </Typography>
                  </Typography>            
                </Box>
              )}

              {!isGstPanMismatch && isGstFirmNameMismatch && (
                <Box data-testid="gst-firm-name-mismatch-warning" sx={bankDetailsOCRStyles.attemptsWarningRow}>
                  <Box sx={bankDetailsOCRStyles.errorIconWrap}>
                    <ErrorAlert />
                  </Box>
                  <Typography sx={bankDetailsOCRStyles.attemptsWarningText}>
                    Firm name on GST does not match your bank account name.{' '}
                    <Typography
                      component="span"
                      onClick={() => {
                        setNameMismatchDetailsScenario('gst_business');
                        setNameMismatchDetailsOpen(true);
                      }}
                      sx={bankDetailsOCRStyles.viewDetailsLink}
                    >
                      {t('view_details')}
                    </Typography>
                  </Typography>
                </Box>
              )}

              {hasGstNameMismatch && (
              <Box data-testid="gst-info-box-mismatch" sx={bankDetailsOCRStyles.gstInfoBox}>
                <Typography sx={bankDetailsOCRStyles.gstInfoText}>
                  Upload a cheque with a matching firm name, or verify using your GST certificate
                </Typography>
              </Box>
              )}

            </>
          )}

          {!showLoader && isGstSectionEnabled && showChequeNameMismatchAlert && !showManualIfsc && !gstCertificateError && isGstVerificationValid && gstValidationMessage && (
            <Box sx={bankDetailsOCRStyles.gstSuccessRow}>
              <CheckCircle sx={bankDetailsOCRStyles.gstSuccessIcon} />
              <Typography sx={bankDetailsOCRStyles.gstSuccessText}>
                {gstValidationMessage}
              </Typography>
            </Box>
          )}

          {uploadedImageSrc && !isBankVerified && bankUploadFailureCount > 0 && !showLoader && error && !showManualIfsc && (
            <Box sx={bankDetailsOCRStyles.attemptsWarningRow}>
              <Box sx={bankDetailsOCRStyles.errorIconWrap}>
                <ErrorAlert />
              </Box>
              <Typography sx={bankDetailsOCRStyles.attemptsWarningText}>
                {error}
              </Typography>
            </Box>
          )}

          {showManualIfsc && (
            <Box data-testid="bank-details-ocr-manual-ifsc-section" sx={bankDetailsOCRStyles.manualIfscSection}>
              {error && !isBankVerified && (
                <Box sx={bankDetailsOCRStyles.manualIfscErrorRow}>
                  <Box sx={bankDetailsOCRStyles.manualIfscErrorIconWrap}>
                    <ErrorAlert />
                  </Box>
                  <Typography sx={bankDetailsOCRStyles.manualIfscErrorText}>
                    {error}
                  </Typography>
                </Box>
              )}

              <Box sx={bankDetailsOCRStyles.manualIfscFieldGroup}>
                <Typography sx={bankDetailsOCRStyles.manualIfscLabel}>
                  IFSC Code <Typography component="span" sx={bankDetailsOCRStyles.manualIfscAsterisk}>*</Typography>
                </Typography>

                <Box sx={bankDetailsOCRStyles.manualIfscInputRow}>
                  <TextField
                    data-testid="bank-details-ocr-manual-ifsc-input"
                    placeholder="Enter IFSC Code"
                    value={manualIfscInput}
                    onChange={(e) => {
                      const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
                      setManualIfscInput(value);
                      if (ifscValidationStatus !== 'idle') {
                        setIfscValidationStatus('idle');
                        setIfscError('');
                        setIfscBankName('');
                        setIfscBranchName('');
                      }
                      if (manualIfscBankError) {
                        setManualIfscBankError('');
                      }
                      if (isBankVerified) {
                        setIsBankVerified(false);
                        setNameMatched(undefined);
                        setVerifiedAccountHolderName('');
                        setValidationMessage('');
                        resetGstStateForNewCheque();
                      }
                    }}
                    error={ifscValidationStatus === 'invalid'}
                    helperText={ifscValidationStatus === 'invalid' ? ifscError : ''}
                    size="small"
                    fullWidth
                    inputProps={{ maxLength: 11 }}
                    sx={bankDetailsOCRStyles.manualIfscTextField}
                  />
                  <Button
                    data-testid="bank-details-ocr-confirm-ifsc-button"
                    variant="outlined"
                    onClick={handleConfirmIfsc}
                    disabled={manualIfscInput.length !== 11 || ifscValidationStatus === 'loading' || ifscValidationStatus === 'valid'}
                    sx={bankDetailsOCRStyles.manualIfscConfirmButton}
                  >
                    {ifscValidationStatus === 'loading' ? <CircularProgress size={20} /> : 'Confirm'}
                  </Button>
                </Box>

                {ifscValidationStatus !== 'invalid' && ifscValidationStatus !== 'valid' && (
                  <Typography sx={bankDetailsOCRStyles.manualIfscHintText}>
                    11-character code printed on your cheque
                  </Typography>
                )}

                {ifscValidationStatus === 'valid' && ifscBankName && (
                  <Typography sx={bankDetailsOCRStyles.manualIfscBankBranchText}>
                    {ifscBankName}{ifscBranchName ? `, ${ifscBranchName}` : ''}
                  </Typography>
                )}
              </Box>

              {manualIfscBankError && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Box sx={bankDetailsOCRStyles.manualIfscErrorIconWrap}>
                    <ErrorAlert />
                  </Box>
                  <Typography sx={{ ...bankDetailsOCRStyles.manualIfscErrorText, color: '#d32f2f' }}>
                    {manualIfscBankError}
                  </Typography>
                </Box>
              )}

              {!showLoader && isBankVerified && validationMessage && !showChequeNameMismatchAlert && (
                <Box sx={{ ...bankDetailsOCRStyles.verifiedRow }}>
                  <CheckCircle sx={bankDetailsOCRStyles.checkIconLarge} />
                  <Box>
                    <Typography sx={bankDetailsOCRStyles.statusText(isBankVerified)}>
                      {validationMessage}
                    </Typography>
                    {verifiedAccountHolderName && (
                      <Typography sx={bankDetailsOCRStyles.verifiedNameText}>
                        {verifiedAccountHolderName}
                      </Typography>
                    )}
                  </Box>
                </Box>
              )}

              {!showLoader && showChequeNameMismatchAlert && !isGstVerificationValid && !hasGstNameMismatch && (
                <Box sx={{ ...bankDetailsOCRStyles.attemptsWarningRow }}>
                  <Box sx={bankDetailsOCRStyles.errorIconWrap}>
                    <ErrorAlert />
                  </Box>
                  <Typography sx={bankDetailsOCRStyles.attemptsWarningText}>
                    {chequeNameMismatchMessage}{' '}
                    <Typography
                      component="span"
                      onClick={() => {
                        setNameMismatchDetailsScenario(establishmentTypeEnum === 'PROPRIETORSHIP' ? 'proprietorship' : 'partnership_company');
                        setNameMismatchDetailsOpen(true);
                      }}
                      sx={bankDetailsOCRStyles.viewDetailsLink}
                    >
                    {t('view_details')}
                    </Typography>
                  </Typography>
                </Box>
              )}

              {!showLoader && isGstSectionEnabled && showChequeNameMismatchAlert && (
                <>
                  {!isGstVerificationValid && !hasGstNameMismatch && (
                    <Box data-testid="gst-info-box-manual" sx={bankDetailsOCRStyles.gstInfoBox}>
                      <Typography sx={bankDetailsOCRStyles.gstInfoText}>
                        Upload a cheque with a matching firm name, or verify using your GST certificate
                      </Typography>
                    </Box>
                  )}

                  <Box sx={bankDetailsOCRStyles.gstSectionTitle}>
                    <Typography sx={bankDetailsOCRStyles.gstSectionTitleText}>
                      Verify with GST Certificate
                    </Typography>
                    <Typography sx={bankDetailsOCRStyles.gstSectionAsterisk}>*</Typography>
                  </Box>

                  <Box sx={bankDetailsOCRStyles.gstUploadStatusGroup}>
                    {gstCertificateObjectKey && gstCertificateFileName ? (
                      <Box data-testid="gst-uploaded-box-manual" sx={bankDetailsOCRStyles.gstUploadedBox}>
                        <Box sx={bankDetailsOCRStyles.gstUploadedIconWrap}>
                          <img src={isGstPdf ? GstPdfIcon : GstImageIcon} alt="GST Certificate" width="32" height="32" />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Typography sx={bankDetailsOCRStyles.gstUploadedFileName}>
                            GST Certificate
                          </Typography>
                          <Typography
                            onClick={!(gstCertificateUploading || isGstVerifying) ? handleGstCertificateView : undefined}
                            sx={{
                              ...bankDetailsOCRStyles.gstUploadedViewLink,
                              ...((gstCertificateUploading || isGstVerifying) ? { opacity: 0.5, pointerEvents: 'none' } : {}),
                            }}
                          >
                            View
                          </Typography>
                        </Box>
                        {isGstVerified ? (
                          <Button
                            data-testid="gst-replace-button-manual"
                            onClick={handleGstCertificateReplace}
                            disabled={gstCertificateUploading || isGstVerifying}
                            sx={bankDetailsOCRStyles.gstUploadedDeleteButton}
                          >
                            Replace
                          </Button>
                        ) : (
                          <IconButton
                            data-testid="gst-delete-button-manual"
                            onClick={handleGstCertificateDelete}
                            disabled={gstCertificateUploading || isGstVerifying}
                            sx={bankDetailsOCRStyles.gstUploadedDeleteButton}
                          >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                              <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                            </svg>
                          </IconButton>
                        )}
                      </Box>
                    ) : (
                      <Box
                        data-testid="gst-upload-box-manual"
                        onClick={!(gstCertificateUploading || isGstVerifying) ? handleGstFileSelect : undefined}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => { if (!(gstCertificateUploading || isGstVerifying) && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleGstFileSelect(); } }}
                        sx={{
                          ...bankDetailsOCRStyles.gstUploadBox,
                          ...((gstCertificateUploading || isGstVerifying) ? { opacity: 0.6, pointerEvents: 'none' } : {}),
                        }}
                      >
                        <Box sx={bankDetailsOCRStyles.gstUploadIconWrap}>
                          <GallerySVG style={{ width: 24, height: 24 }} />
                        </Box>
                        <Box>
                          <Typography sx={bankDetailsOCRStyles.gstUploadLabel}>Upload GST Certificate</Typography>
                          <Typography sx={bankDetailsOCRStyles.gstUploadSubtext}>JPG, PDF</Typography>
                        </Box>
                      </Box>
                    )}

                    {(gstCertificateUploading || isGstVerifying) && (
                      <Box data-testid="gst-verifying-status-manual" sx={bankDetailsOCRStyles.inlineLoaderOverlay}>
                        <Box sx={bankDetailsOCRStyles.inlineLoaderIconWrap}>
                          <CircularProgress size={22} sx={{ color: '#16A34A' }} />
                        </Box>
                        <Typography sx={bankDetailsOCRStyles.inlineLoaderStatusText}>
                          {gstCertificateUploading ? 'Uploading GST certificate...' : 'Verifying GST details...'}
                        </Typography>
                      </Box>
                    )}

                    {gstCertificateError && (
                      <Typography sx={bankDetailsOCRStyles.gstErrorText}>
                        {gstCertificateError}
                      </Typography>
                    )}
                  </Box>

                  {isGstPanMismatch && (
                    <Box data-testid="gst-pan-mismatch-warning-manual" sx={bankDetailsOCRStyles.attemptsWarningRow}>
                      <Box sx={bankDetailsOCRStyles.errorIconWrap}>
                        <ErrorAlert />
                      </Box>
                      <Typography sx={bankDetailsOCRStyles.attemptsWarningText}>
                        PAN number on your GST does not match the uploaded PAN.{' '}
                        <Typography
                          component="span"
                          onClick={() => {
                            setNameMismatchDetailsScenario('gst_pan');
                            setNameMismatchDetailsOpen(true);
                          }}
                          sx={bankDetailsOCRStyles.viewDetailsLink}
                        >
                          {t('view_details')}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  {!isGstPanMismatch && isGstFirmNameMismatch && (
                    <Box data-testid="gst-firm-name-mismatch-warning-manual" sx={bankDetailsOCRStyles.attemptsWarningRow}>
                      <Box sx={bankDetailsOCRStyles.errorIconWrap}>
                        <ErrorAlert />
                      </Box>
                      <Typography sx={bankDetailsOCRStyles.attemptsWarningText}>
                        Firm name on GST does not match your bank account name.{' '}
                        <Typography
                          component="span"
                          onClick={() => {
                            setNameMismatchDetailsScenario('gst_business');
                            setNameMismatchDetailsOpen(true);
                          }}
                          sx={bankDetailsOCRStyles.viewDetailsLink}
                        >
                          {t('view_details')}
                        </Typography>
                      </Typography>
                    </Box>
                  )}

                  {hasGstNameMismatch && (
                  <Box data-testid="gst-info-box-manual-mismatch" sx={bankDetailsOCRStyles.gstInfoBox}>
                    <Typography sx={bankDetailsOCRStyles.gstInfoText}>
                      Upload a cheque with a matching firm name, or verify using your GST certificate
                    </Typography>
                  </Box>
                  )}

                </>
              )}

              {!showLoader && isGstSectionEnabled && showChequeNameMismatchAlert && !gstCertificateError && isGstVerificationValid && gstValidationMessage && (
                <Box sx={bankDetailsOCRStyles.gstSuccessRow}>
                  <CheckCircle sx={bankDetailsOCRStyles.gstSuccessIcon} />
                  <Typography sx={bankDetailsOCRStyles.gstSuccessText}>
                    {gstValidationMessage}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          <input
            ref={gstFileInputRef}
            type="file"
            accept="image/jpeg,image/png,application/pdf,.jpg,.jpeg,.png,.pdf"
            onChange={handleGstFileChange}
            style={{ display: 'none' }}
            data-testid="gst-file-input"
          />
        </Box>
      </Box>

      <Box className="bank-details-ocr-bottom-bar" data-testid="bank-details-ocr-bottom-bar" sx={bankDetailsOCRStyles.bottomBar}>
        <Box className="bank-details-ocr-back-button" data-testid="bank-details-ocr-back-button" onClick={onBack} sx={bankDetailsOCRStyles.backButton}>
          <ArrowBack className="bank-details-ocr-back-icon" data-testid="bank-details-ocr-back-icon" sx={bankDetailsOCRStyles.backIcon} />
          <Typography className="bank-details-ocr-back-text" data-testid="bank-details-ocr-back-text" sx={bankDetailsOCRStyles.backText}>
            {t('back')}
          </Typography>
        </Box>

        <Box
          className="bank-details-ocr-primary-button"
          data-testid="bank-details-ocr-primary-button"
          onClick={isPrimaryEnabled ? handleNext : undefined}
          sx={bankDetailsOCRStyles.primaryButton(isPrimaryEnabled)}
        >
          <Typography className="bank-details-ocr-primary-button-text" data-testid="bank-details-ocr-primary-button-text" sx={bankDetailsOCRStyles.primaryButtonText(isPrimaryEnabled)}>
            {primaryCtaLabel}
          </Typography>
        </Box>
      </Box>

      <WebcamCaptureOverlay webcam={webcam} />

      <DocumentTipsModal
        open={tipsModalOpen}
        documentType="cheque"
        action={tipsAction}
        onClose={() => setTipsModalOpen(false)}
        onGotIt={handleTipsGotIt}
      />

      <DocumentViewerModal
        open={modalOpen}
        onClose={handleModalClose}
        imageSrc={uploadedImageSrc}
        onVerify={handleVerifyWrapper}
      />

      <UpdateBankModal
        open={deleteModalOpen}
        onClose={handleEditCancel}
        onUploadPhoto={handleEditUploadPhoto}
        onTakePhoto={handleEditTakePhoto}
        panNumber={panNumber}
        linkedFirms={associatedCustomerDataList}
        currentCustomerId={info?.customerId}
        currentCustomerShopName={info?.shopName}
      />

      <ReasonForMismatchModal
        open={reasonModalOpen}
        onClose={handleReasonModalClose}
        selectedReason={reasonForMismatch}
        onSelect={handleReasonSelect}
      />

      <NameMismatchDetailsModal
        open={nameMismatchDetailsOpen}
        onClose={() => setNameMismatchDetailsOpen(false)}
        scenario={nameMismatchDetailsScenario}
        expectedName={
          nameMismatchDetailsScenario === 'gst_pan'
            ? panNumberForComparison
            : nameMismatchDetailsScenario === 'gst_business'
              ? gstFirmName
              : establishmentTypeEnum === 'PROPRIETORSHIP'
                ? panNameForComparison
                : firmNameForComparison
        }
        chequeName={nameMismatchDetailsScenario === 'gst_pan' ? gstPanForComparison : chequeNameForComparison}
        panName={panNameForComparison}
      />

      {associatedCustomerDataList && associatedCustomerDataList.length > 0 && (
        <AssociationModal
          open={associationDetailsOpen}
          panNumber={panNumber}
          currentCustomerId={info?.customerId}
          currentCustomerShopName={info?.shopName}
          associatedCustomerDataList={associatedCustomerDataList.map((item) => ({
            customerId: item.customerId,
            shopName: item.firmName,
          }))}
          bankDetails={{
            bankName: String(bankSlice?.bankResponse?.bankName || bankSlice?.bankResponse?.bank || ifscSlice?.bankDetails?.bank || (info?.bankDetails as any)?.bankName || ''),
            bankAccountNumber: String(bankSlice?.bankResponse?.bankAccountNumber || (info?.bankDetails as any)?.bankAccountNumber || ''),
            ifscCode: String(bankSlice?.bankResponse?.ifscCode || (info?.bankDetails as any)?.ifscCode || ''),
          }}
          onClose={() => setAssociationDetailsOpen(false)}
        />
      )}

      <GstDocumentViewerModal
        open={gstDocumentViewerOpen}
        onClose={() => setGstDocumentViewerOpen(false)}
        documentUrl={gstCertificatePublicUrl || (gstCertificateFile ? URL.createObjectURL(gstCertificateFile) : null)}
        isPdf={isGstPdf}
      />
    </Box>
  );
}
